import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  CREATOR_NOTIFICATION_CATALOGUE,
  getDefaultPolicy,
  type CreatorNotificationEvent,
  type CreatorNotificationPolicy,
} from '@/config/creatorNotificationCatalogue'

export type PolicyScope = 'default' | string

type CampaignPolicyMap = Record<CreatorNotificationEvent, CreatorNotificationPolicy>
type ScopePolicyMap = Record<PolicyScope, Partial<CampaignPolicyMap>>

const DEFAULT_SCOPE: PolicyScope = 'default'

function buildCatalogueDefault(): CampaignPolicyMap {
  const out = {} as CampaignPolicyMap
  for (const entry of CREATOR_NOTIFICATION_CATALOGUE) {
    out[entry.id] = { ...entry.defaultPolicy }
  }
  return out
}

function normalizePolicy(p: CreatorNotificationPolicy): CreatorNotificationPolicy {
  return {
    inApp: p.inApp,
    email: p.email,
  }
}

export const useNotificationPoliciesStore = defineStore('notificationPolicies', () => {
  const scopes = ref<ScopePolicyMap>({ [DEFAULT_SCOPE]: buildCatalogueDefault() })
  const activeScope = ref<PolicyScope>(DEFAULT_SCOPE)

  const activePolicy = computed<CampaignPolicyMap>(() => {
    const explicit = scopes.value[activeScope.value] ?? {}
    const fallback = scopes.value[DEFAULT_SCOPE] ?? {}
    const merged = {} as CampaignPolicyMap
    for (const entry of CREATOR_NOTIFICATION_CATALOGUE) {
      merged[entry.id] =
        explicit[entry.id] ?? fallback[entry.id] ?? getDefaultPolicy(entry.id)
    }
    return merged
  })

  function setScope(scope: PolicyScope) {
    activeScope.value = scope
    if (!scopes.value[scope]) {
      scopes.value[scope] = {}
    }
  }

  function getPolicy(
    scope: PolicyScope,
    eventId: CreatorNotificationEvent
  ): CreatorNotificationPolicy {
    return (
      scopes.value[scope]?.[eventId] ??
      scopes.value[DEFAULT_SCOPE]?.[eventId] ??
      getDefaultPolicy(eventId)
    )
  }

  function setPolicy(
    scope: PolicyScope,
    eventId: CreatorNotificationEvent,
    policy: CreatorNotificationPolicy
  ) {
    if (!scopes.value[scope]) {
      scopes.value[scope] = {}
    }
    scopes.value[scope]![eventId] = normalizePolicy(policy)
  }

  function resetScope(scope: PolicyScope) {
    if (scope === DEFAULT_SCOPE) {
      scopes.value[DEFAULT_SCOPE] = buildCatalogueDefault()
    } else {
      scopes.value[scope] = {}
    }
  }

  function hasOverride(scope: PolicyScope, eventId: CreatorNotificationEvent): boolean {
    if (scope === DEFAULT_SCOPE) return false
    return Boolean(scopes.value[scope]?.[eventId])
  }

  function clearOverride(scope: PolicyScope, eventId: CreatorNotificationEvent) {
    if (scope === DEFAULT_SCOPE) return
    const map = scopes.value[scope]
    if (map && eventId in map) {
      delete map[eventId]
    }
  }

  return {
    scopes,
    activeScope,
    activePolicy,
    setScope,
    getPolicy,
    setPolicy,
    resetScope,
    hasOverride,
    clearOverride,
  }
})
